// Hydrates the notification store on auth + provides ack mutation.
// JetStream keeps it live afterwards via the ordered consumer in
// hooks/useJsConsumer.ts (alerts ride the same stream as sensor + log
// data; envelope.ts decodes alerts.fired and ingestQueue routes them
// to the notification store).

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { withCompany } from '@/lib/withCompany';
import { API_BASE } from '@/apiConfig';
import type { AlertEvent } from '@/types';

interface ListResponse { events: AlertEvent[]; total?: number }

export function useAlertEventsHydration() {
  const token   = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  const hydrate = useNotificationStore((s) => s.hydrate);

  const { data } = useQuery({
    queryKey: ['alert-events', 'unacked', company],
    // Company-scoped: 400s without a company (admin who hasn't picked one yet),
    // so never fire it company-less.
    enabled:  !!token && !!company,
    staleTime: 60_000,
    queryFn: async (): Promise<AlertEvent[]> => {
      const url = withCompany(`${API_BASE}/alert-events?unacked_only=true&limit=50`, company);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`alert-events ${res.status}`);
      const body = await res.json() as ListResponse | AlertEvent[];
      return Array.isArray(body) ? body : (body.events ?? []);
    },
  });

  useEffect(() => {
    if (data) hydrate(data);
  }, [data, hydrate]);
}

export function useAckEvent() {
  const token  = useAuthStore((s) => s.user?.token ?? null);
  const user   = useAuthStore((s) => s.user);
  const ack    = useNotificationStore((s) => s.ackLocal);
  const qc     = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      const res = await fetch(`${API_BASE}/alert-events/${id}/ack`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify(note ? { note } : {}),
      });
      if (!res.ok) throw new Error(`ack ${res.status}`);
      return res.json();
    },
    onMutate: async ({ id }) => {
      ack(id, user?.email ?? '');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['alert-events'] }),
  });
}

export function useAckAll() {
  const token  = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  const user   = useAuthStore((s) => s.user);
  const ack    = useNotificationStore((s) => s.ackLocal);
  const qc     = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const url = withCompany(`${API_BASE}/alert-events/ack-all`, company);
      const res = await fetch(url, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`ack-all ${res.status}`);
      return res.json();
    },
    onMutate: async () => {
      const ids = Object.keys(useNotificationStore.getState().events);
      for (const id of ids) ack(id, user?.email ?? '');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['alert-events'] }),
  });
}
